import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { SegmentedControl } from '~/components/segmented-control';

/**
 * Mass-edit payload. Each field is tri-state: `null` = leave unchanged, otherwise the new value.
 * Mirrors the QuickEdit value shape (visibility / availability / featured / used / social) and
 * maps onto `BulkEditTokenPost` in the caller.
 */
export interface MassEditValue {
	visible: boolean | null;
	featured: boolean | null;
	used: boolean | null;
	social: boolean | null;
	/** AvailabilityStatus enum id, or null to leave unchanged. */
	availability: number | null;
}

const EMPTY: MassEditValue = { visible: null, featured: null, used: null, social: null, availability: null };

interface MassEditDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Number of selected products (shown in the header + apply button). */
	count: number;
	availabilityOptions: { value: string; label: string }[];
	pending: boolean;
	onApply: (value: MassEditValue) => void;
}

type Tri = 'keep' | 'on' | 'off';

function triToBool(t: Tri): boolean | null {
	return t === 'on' ? true : t === 'off' ? false : null;
}

const AVAILABILITY_KEEP = '__keep__';

/** A single tri-state boolean flag row ("Leave" / "On" / "Off"). */
function FlagRow({ label, value, onChange }: { label: string; value: Tri; onChange: (t: Tri) => void }) {
	const { t } = useTranslation();
	const options = [
		{ value: 'keep' as const, label: t('products.massEdit.keep') },
		{ value: 'on' as const, label: t('common.yes') },
		{ value: 'off' as const, label: t('common.no') }
	];
	return (
		<div className="flex items-center justify-between gap-3">
			<Label className="text-sm font-medium text-foreground">{label}</Label>
			<SegmentedControl value={value} onValueChange={onChange} options={options} size="sm" />
		</div>
	);
}

/**
 * Mass-edit dialog: pick which fields to change across the selected products. Tri-state flags
 * default to "leave unchanged" so an empty submit is a no-op. Disabled until at least one field
 * is set. Applies via `bulkEditPost` in the caller.
 */
export function MassEditDialog({ open, onOpenChange, count, availabilityOptions, pending, onApply }: MassEditDialogProps) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState<Tri>('keep');
	const [featured, setFeatured] = useState<Tri>('keep');
	const [used, setUsed] = useState<Tri>('keep');
	const [social, setSocial] = useState<Tri>('keep');
	const [availability, setAvailability] = useState<string>(AVAILABILITY_KEEP);

	// Reset the form each time the dialog opens.
	useEffect(() => {
		if (open) {
			setVisible('keep');
			setFeatured('keep');
			setUsed('keep');
			setSocial('keep');
			setAvailability(AVAILABILITY_KEEP);
		}
	}, [open]);

	const value: MassEditValue = {
		visible: triToBool(visible),
		featured: triToBool(featured),
		used: triToBool(used),
		social: triToBool(social),
		availability: availability === AVAILABILITY_KEEP ? null : Number(availability)
	};
	const hasChange =
		value.visible !== null || value.featured !== null || value.used !== null || value.social !== null || value.availability !== null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('products.massEdit.title')}</DialogTitle>
					<DialogDescription>{t('products.massEdit.subtitle', { count })}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					<FlagRow label={t('products.quickEdit.visibleTitle')} value={visible} onChange={setVisible} />
					<FlagRow label={t('products.quickEdit.featured')} value={featured} onChange={setFeatured} />
					<FlagRow label={t('products.quickEdit.used')} value={used} onChange={setUsed} />
					<FlagRow label={t('products.quickEdit.socialFeeds')} value={social} onChange={setSocial} />

					<div className="flex flex-col gap-1.5">
						<Label className="text-sm font-medium text-foreground">{t('products.quickEdit.availabilitySection')}</Label>
						<Select value={availability} onValueChange={setAvailability}>
							<SelectTrigger className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={AVAILABILITY_KEEP}>{t('products.massEdit.keep')}</SelectItem>
								{availabilityOptions.map(opt => (
									<SelectItem key={opt.value} value={opt.value}>
										{opt.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button onClick={() => onApply(value)} disabled={!hasChange || pending || count === 0}>
						{t('products.massEdit.apply', { count })}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export { EMPTY as EMPTY_MASS_EDIT };
